import { Box, IconButton } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import PropTypes from "prop-types";
import { DeleteOutlined, EditOutlined } from "@mui/icons-material";
import { getPositionPercentage, getPositionValue } from "../dashboardUtil";

export const DashboardComment = (props) => {
  const { position, element, parentEl, onLabelCreated, onLabelDeleted, onLabelPosUpdated, triggerEdit, label, isUpdate } = props;
  const [dragPos, setDragPos] = useState(position);
  const [resizePos, setResizePos] = useState(position);
  const [text, setText] = useState(label?.text ?? "");
  const [showControls, setShowControls] = useState(false);
  const itemClass = "handle-" + (label?.id ?? "default");

  const dlgRef = useRef(null);
  const isDragged = useRef(null);
  const isMouseDownDelete = useRef(null);

  useEffect(() => {
    if (isUpdate && !label) {
      setResizePos(position);
    }

    const resizeHandler = () => {
      setResizePos(getPositionValue(position, element));
    }
    window.addEventListener("resize", resizeHandler);

    return () => window.removeEventListener("resize", resizeHandler);
  }, []);

  useEffect(() => {
    if ((isUpdate && !label) || !element) return;

    const pos = getPositionValue(position, element);
    setResizePos(pos);
  }, [element]);

  useEffect(() => {
    if (isUpdate) return;

    onLabelPosUpdated({ ...label, position: dragPos});
  }, [dragPos]);

  const onDragStarted = (e, data) => {
    isDragged.current = false;
  }

  const onDragging = (e, data) => {
    isDragged.current = true;
  }

  const onDragStopped = (e, data) => {
    if (!isDragged.current && !isMouseDownDelete.current) return;

    const bounds = element.getBoundingClientRect();
    const dlgBounds = dlgRef.current?.getBoundingClientRect();
    const pos = { x: dlgBounds.left - bounds.left, y: dlgBounds.top - bounds.top }; 
    setResizePos(pos);

    const posPer = getPositionPercentage(pos, element);
    setDragPos(posPer);
  };

  const onMouseDownDelete = (event) => {
    isMouseDownDelete.current = true;
  }

  const onInput = (event) => {
    setText(event.target.value);
  }

  const onBlur = () => {
    const posPer = getPositionPercentage(dragPos, element);
    onLabelCreated({ ...label, text, position: posPer });
  };

  return (
    <>
      <Box component={"div"}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        <Draggable
          axis="both"
          handle={`.${itemClass}`}
          position={{
            x: resizePos.x + 24,
            y: resizePos.y,
          }}
          scale={1}
          onStart={onDragStarted}
          onDrag={onDragging}
          onStop={onDragStopped}
          disabled={isUpdate}
          bounds={parentEl && element ? {
            left: 0, 
            top: 0, 
            right: parentEl.getBoundingClientRect().width - (dlgRef.current?.getBoundingClientRect().width || 0), 
            bottom: element.getBoundingClientRect().height - (dlgRef.current?.getBoundingClientRect().height || 0)
          } : undefined}
        >
          {isUpdate ? 
            <Box component={"div"} className={itemClass}
              sx={{
                width: "250px",
                height: "130px",
                background: "#1b202a",
                borderRadius: "8px",
                opacity: 1,
                position: "absolute",
                top: 0,
                left: resizePos.x + 250 > element.getBoundingClientRect().width ? element.getBoundingClientRect().width - (resizePos.x + 250) : 0,
                padding: "6px 8px",
                color: "white",
                display: "flex",
                flexDirection: "column",
                alignItems: "end",
              }}
              ref={dlgRef}
            >
              <textarea onChange={onInput} 
                name="inputText"
                style={{ 
                  width: "100%", 
                  height: "95px", 
                  resize: "none", 
                  background: "#36485a", 
                  border: "none",
                  opacity: 0.8, 
                  color: "white",
                  padding: "4px",
                }}
                autoFocus
                onBlur={onBlur}
                value={text}
              >
              </textarea>
            </Box> :
            <Box component={"div"} className={itemClass}
              sx={{
                display: "flex",
              }}
              onMouseEnter={() => setShowControls(true)}
              onMouseLeave={() => setShowControls(false)}
              ref={dlgRef}
            >
              <Box component={"div"}
                sx={{
                  color: "white",
                  userSelect: "none",
                  background: "#36485a", 
                  border: "1px solid white",
                  opacity: 0.7, 
                  padding: "4px 8px",
                  borderRadius: "8px",
                  whiteSpace: "pre-wrap",
                  minHeight: "48px",
                  minWidth: "64px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {label.text}
              </Box>
              {showControls && <Box component={"div"}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <IconButton size="small" style={{ transform: "scale(0.6)", padding: 0 }} onClick={() => triggerEdit(label)}>
                  <EditOutlined style={{ color: "white" }} />
                </IconButton>
                <IconButton size="small" style={{ transform: "scale(0.6)", padding: 0 }} 
                    onClick={() => onLabelDeleted(label)} 
                    onMouseDown={onMouseDownDelete}
                    onMouseUp={() => onLabelDeleted(label)}>
                  <DeleteOutlined style={{ color: "red" }} />
                </IconButton>
              </Box>}
            </Box>
          }
        </Draggable>
      </Box>
    </>
  );
};

DashboardComment.propTypes = {
  position: PropTypes.object,
  element: PropTypes.object,
  parentEl: PropTypes.object,
  onLabelCreated: PropTypes.func,
  onLabelDeleted: PropTypes.func,
  onLabelPosUpdated: PropTypes.func,
  triggerEdit: PropTypes.func,
  label: PropTypes.object,
  isUpdate: PropTypes.bool,
}